import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  AuthLayout, AuthHeading, AuthField, PasswordField, AuthError, AuthPrimary,
  AuthSecondary, AuthLink, BackLink, StatusIcon, MailIcon, AuthFooter, footerLinkStyle,
} from '../components/AuthShell'

export default function Login() {
  const { signInWithPassword, signUp, sendPasswordResetEmail, sendMagicLink } = useAuth()
  const [screen, setScreen] = useState('login') // 'login' | 'signup' | 'forgot' | 'sent' | 'newclient' | 'magic' | 'magic-sent'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [forgotEmail, setForgotEmail] = useState('')
  const [magicEmail, setMagicEmail] = useState('')
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '', confirm: '' })
  const [signupDone, setSignupDone] = useState(false)

  function go(next) { setScreen(next); setError('') }

  async function handleLogin(e) {
    e.preventDefault()
    if (!email || !password) { setError('Enter your email and password to sign in.'); return }
    setLoading(true)
    setError('')
    try {
      const { error } = await signInWithPassword(email, password)
      if (error) {
        setError("That email and password don't match. Try again or use a sign-in link.")
      }
    } catch {
      setError('Connection error. Check your internet and try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSignup(e) {
    e.preventDefault()
    const { name, email, password, confirm } = signupForm
    if (!name || !email || !password) return
    if (password !== confirm) { setError('Passwords do not match.'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return }
    setLoading(true)
    setError('')
    try {
      const { error } = await signUp(name, email, password)
      if (error) { setError(error.message || 'Something went wrong. Please try again.'); return }
      setSignupDone(true)
    } catch {
      setError('Connection error. Check your internet and try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleForgot(e) {
    e.preventDefault()
    if (!forgotEmail) return
    setLoading(true)
    setError('')
    await sendPasswordResetEmail(forgotEmail)
    setLoading(false)
    setScreen('sent')
  }

  async function sendLink(address) {
    setLoading(true)
    setError('')
    try {
      const { error } = await sendMagicLink(address)
      if (error) { setError('Could not send link. Make sure this email matches your account.'); return false }
      setScreen('magic-sent')
      return true
    } catch {
      setError('Connection error. Check your internet and try again.')
      return false
    } finally {
      setLoading(false)
    }
  }

  async function handleMagicLink(e) {
    e.preventDefault()
    if (!magicEmail) return
    await sendLink(magicEmail)
  }

  // "Email me a sign-in link instead": with an email already typed, send it
  // straight away; without one, ask for it on the magic-link screen.
  async function handleMagicShortcut() {
    setMagicEmail(email)
    if (!email) { go('magic'); return }
    const ok = await sendLink(email)
    if (!ok) setScreen('magic')
  }

  return (
    <AuthLayout>
      {screen === 'login' && (
        <form onSubmit={handleLogin} aria-labelledby="signin-title" noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AuthHeading id="signin-title" title="Sign in" sub="Welcome back." />

          <AuthField label="Email" type="email" placeholder="you@company.com" value={email}
            onChange={e => setEmail(e.target.value)} required autoComplete="email"
            autoCapitalize="none" autoCorrect="off" invalid={!!error && !email} />

          <PasswordField
            aside={<button type="button" onClick={() => { go('forgot'); setForgotEmail(email) }} style={footerLinkStyle}>Forgot password?</button>}
            placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
            required autoComplete="current-password" invalid={!!error && !!email} />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--txt)', cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
              style={{ width: 16, height: 16, margin: 0, accentColor: '#112B32' }} />
            Keep me signed in
          </label>

          <AuthError>{error}</AuthError>

          <AuthPrimary loading={loading} loadingLabel="Signing in…" disabled={!email || !password}>Sign in</AuthPrimary>
          <AuthSecondary icon={MailIcon} onClick={handleMagicShortcut} disabled={loading}>Email me a sign-in link instead</AuthSecondary>

          <AuthFooter>
            <span>New client? <button type="button" onClick={() => { go('signup'); setSignupDone(false) }} style={footerLinkStyle}>Request access</button></span>
            <span>Contractor? <Link to="/contractor-login" style={footerLinkStyle}>Contractor sign-in</Link></span>
          </AuthFooter>
        </form>
      )}

      {screen === 'forgot' && (
        <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BackLink onClick={() => go('login')} />
          <AuthHeading title="Forgot your password?" sub="Enter your email and we'll send a link to set a new one." />
          <AuthField label="Email" type="email" placeholder="you@company.com" value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)} required autoFocus autoComplete="email" />
          <AuthError>{error}</AuthError>
          <AuthPrimary loading={loading} loadingLabel="Sending…" disabled={!forgotEmail}>Send reset link</AuthPrimary>
        </form>
      )}

      {screen === 'signup' && !signupDone && (
        <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BackLink onClick={() => go('login')} />
          <AuthHeading title="Request access" sub="Working with us but don't have a login? Send your details and you'll get access once your request is approved." />
          <AuthField label="Full name" placeholder="Jane Smith" required autoFocus autoComplete="name"
            value={signupForm.name} onChange={e => setSignupForm(f => ({ ...f, name: e.target.value }))} />
          <AuthField label="Work email" type="email" placeholder="you@company.com" required autoComplete="email"
            value={signupForm.email} onChange={e => setSignupForm(f => ({ ...f, email: e.target.value }))} />
          <PasswordField label="Create a password" placeholder="At least 6 characters" required autoComplete="new-password"
            value={signupForm.password} onChange={e => setSignupForm(f => ({ ...f, password: e.target.value }))} />
          <PasswordField label="Confirm password" placeholder="Re-enter password" required autoComplete="new-password"
            invalid={!!signupForm.confirm && signupForm.confirm !== signupForm.password}
            value={signupForm.confirm} onChange={e => setSignupForm(f => ({ ...f, confirm: e.target.value }))} />
          <AuthError>{error}</AuthError>
          <AuthPrimary loading={loading} loadingLabel="Sending request…">Send request</AuthPrimary>
        </form>
      )}

      {screen === 'signup' && signupDone && (
        <>
          <StatusIcon kind="check" />
          <AuthHeading title="Request submitted" sub="Your account is pending approval. We'll email you once you've been granted access. Nothing else to do for now." />
          <AuthLink onClick={() => go('login')}>Back to sign in</AuthLink>
        </>
      )}

      {screen === 'newclient' && (
        <>
          <BackLink onClick={() => go('login')} />
          <AuthHeading title="Let's get you set up" sub="Client accounts are created by our team once your project is confirmed. Reach out and we'll get your portal ready." />
          <a href="mailto:efren@abstract-creative.com?subject=New Client Portal Access&body=Hi Efren, I'd like to get access to the client portal."
            style={{ height: 50, borderRadius: 10, background: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)', fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {MailIcon}Email us to get started
          </a>
          <a href="https://abstract-creative.com" target="_blank" rel="noreferrer"
            style={{ height: 44, borderRadius: 10, background: 'var(--surf)', color: 'var(--navy)', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Learn more about Abstract Creative
          </a>
        </>
      )}

      {screen === 'magic' && (
        <form onSubmit={handleMagicLink} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BackLink onClick={() => go('login')} />
          <AuthHeading title="Email me a sign-in link" sub="Enter your email and we'll send a one-click link to sign in. No password needed." />
          <AuthField label="Email" type="email" placeholder="you@company.com" value={magicEmail}
            onChange={e => setMagicEmail(e.target.value)} required autoFocus autoComplete="email" />
          <AuthError>{error}</AuthError>
          <AuthPrimary loading={loading} loadingLabel="Sending…" disabled={!magicEmail}>Send sign-in link</AuthPrimary>
        </form>
      )}

      {screen === 'magic-sent' && (
        <>
          <StatusIcon kind="mail" />
          <AuthHeading title="Check your email" />
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--txt)' }}>
            We sent a sign-in link to <strong>{magicEmail}</strong>. It works once and expires in 60 minutes. Don't see it? Check your spam or junk folder.
          </p>
          <AuthError>{error}</AuthError>
          <button type="button" onClick={() => sendLink(magicEmail)} disabled={loading}
            style={{ height: 46, border: '1px solid var(--bdr2)', borderRadius: 10, background: 'var(--card)', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer' }}>
            {loading ? 'Sending…' : 'Resend link'}
          </button>
          <AuthLink onClick={() => go('login')}>Use a password instead</AuthLink>
        </>
      )}

      {screen === 'sent' && (
        <>
          <StatusIcon kind="check" />
          <AuthHeading title="Check your email" />
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--txt)' }}>
            We sent a password reset link to <strong>{forgotEmail}</strong>. Click it to set a new password. The link expires in 60 minutes.
          </p>
          <AuthLink onClick={() => go('login')}>Back to sign in</AuthLink>
        </>
      )}
    </AuthLayout>
  )
}
